import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Phone, Instagram, Facebook, MapPin, Mail, Send, Loader2 } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import {
  WHATSAPP_URL, PHONE_DISPLAY, INSTAGRAM_HANDLE, INSTAGRAM_URL, FACEBOOK_URL, EMAIL, CITY,
} from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES = ["Handyman & Repairs", "Residential Cleaning", "Commercial Cleaning", "Not sure yet"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Pre-select the service when a "Request This Service" card button is clicked
  useEffect(() => {
    const handler = (e) => {
      if (e.detail) setForm((f) => ({ ...f, service: e.detail }));
    };
    window.addEventListener("select-service", handler);
    return () => window.removeEventListener("select-service", handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service) {
      toast.error("Please fill in your name, phone and the service you need.");
      return;
    }
    setLoading(true);
    try {
      const subject = form.service ? `${form.service} Request` : "General Inquiry";
      await axios.post(`${API}/contact`, { ...form, subject });
      toast.success("Thank you! We received your request and will reach out shortly.");
      setForm({ name: "", phone: "", service: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or message us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-offwhite border border-ink/20 px-4 py-3.5 text-ink placeholder:text-ink/40 focus:outline-none focus:border-mustard focus:ring-2 focus:ring-mustard/40 transition-[border-color,box-shadow] duration-300";
  const labelCls = "block text-[11px] font-semibold tracking-[0.12em] uppercase text-graphite mb-2";

  return (
    <section id="contact" className="relative py-20 md:py-32 bg-offwhite" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-12">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-16">
          {/* Form */}
          <Reveal>
            <p className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-petrol mb-4">Get a Free Quote</p>
            <h2 className="font-display font-extrabold text-ink text-3xl md:text-5xl tracking-tight leading-tight mb-8">
              Tell us what your home needs.
            </h2>

            <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls} htmlFor="name">Name</label>
                  <input id="name" data-testid="contact-name" className={inputCls} value={form.name} onChange={update("name")} placeholder="Your full name" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="phone">Phone</label>
                  <input id="phone" data-testid="contact-phone" className={inputCls} value={form.phone} onChange={update("phone")} placeholder="(704) 000-0000" />
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="service">Service Needed</label>
                <select id="service" data-testid="contact-service" className={inputCls} value={form.service} onChange={update("service")}>
                  <option value="" disabled>Select a service</option>
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="message">Message</label>
                <textarea id="message" data-testid="contact-message" rows={4} className={`${inputCls} resize-none`} value={form.message} onChange={update("message")} placeholder="Tell us a bit about the project..." />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="inline-flex items-center gap-2.5 rounded-full bg-petrol px-8 py-4 font-semibold text-offwhite hover:bg-ink hover:scale-[1.02] transition-[transform,background-color] duration-300 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" strokeWidth={2.2} />}
                {loading ? "Sending..." : "Send Request"}
              </button>
            </form>
          </Reveal>

          {/* Direct contact panel */}
          <Reveal delay={0.15}>
            <div className="rounded-[28px] bg-petrol text-offwhite p-8 md:p-10 h-full flex flex-col">
              <h3 className="font-display font-bold text-2xl mb-2">Prefer to chat now?</h3>
              <p className="text-offwhite/70 font-light mb-8">Message us directly on WhatsApp for the fastest, no-obligation quote.</p>

              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="inline-flex items-center justify-center gap-3 rounded-full bg-mustard px-7 py-4 font-semibold text-ink hover:scale-[1.03] hover:shadow-[0_12px_40px_rgba(232,163,23,0.45)] transition-transform duration-300"
              >
                <MessageCircle className="h-5 w-5" strokeWidth={2.4} /> Chat on WhatsApp
              </a>

              <div className="mt-10 space-y-5">
                <a href={`tel:+${PHONE_DISPLAY.replace(/\D/g, "")}`} className="flex items-center gap-4 group" data-testid="contact-phone-link">
                  <span className="h-11 w-11 rounded-xl bg-offwhite/10 flex items-center justify-center group-hover:bg-mustard group-hover:text-ink transition-colors duration-300"><Phone className="h-5 w-5" /></span>
                  <div><p className="text-xs text-offwhite/50 uppercase tracking-wider">Phone</p><p className="font-medium">{PHONE_DISPLAY}</p></div>
                </a>
                <a href={`mailto:${EMAIL}`} className="flex items-center gap-4 group" data-testid="contact-email-link">
                  <span className="h-11 w-11 rounded-xl bg-offwhite/10 flex items-center justify-center group-hover:bg-mustard group-hover:text-ink transition-colors duration-300"><Mail className="h-5 w-5" /></span>
                  <div><p className="text-xs text-offwhite/50 uppercase tracking-wider">Email</p><p className="font-medium">{EMAIL}</p></div>
                </a>
                <div className="flex items-center gap-4">
                  <span className="h-11 w-11 rounded-xl bg-offwhite/10 flex items-center justify-center"><MapPin className="h-5 w-5" /></span>
                  <div><p className="text-xs text-offwhite/50 uppercase tracking-wider">Area</p><p className="font-medium">{CITY} & surrounding</p></div>
                </div>
              </div>

              <div className="mt-auto pt-10 flex items-center gap-3">
                <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="social-instagram" className="h-11 w-11 rounded-xl bg-offwhite/10 flex items-center justify-center hover:bg-mustard hover:text-ink transition-colors duration-300"><Instagram className="h-5 w-5" /></a>
                <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" data-testid="social-facebook" className="h-11 w-11 rounded-xl bg-offwhite/10 flex items-center justify-center hover:bg-mustard hover:text-ink transition-colors duration-300"><Facebook className="h-5 w-5" /></a>
                <span className="text-sm text-offwhite/60 ml-2">{INSTAGRAM_HANDLE}</span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
